import React, { useEffect, useState } from 'react';

import { Box } from '@mui/material';
import { ChipButton } from '@apigrowfinance/pbolt-chip-button';
import DateFormatter from '@apigrowfinance/pbolt-date-formatter';
import GenericLoadingText from '@apigrowfinance/pbolt-generic-loading-text';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import NoTimeline from '@components/accountTimeline/skeletons/NoTimeline';
import TimelineSkeleton from '@components/accountTimeline/skeletons/TimelineSkeleton';
import Timeline from '@components/accountTimeline/Timeline';
import TimelineFilterDialog from '@components/accountTimeline/timelineFilter/TimelineFilterModal';
import EndMessage from '@components/infiniteScroll/EndMessage';
import LoadingError from '@components/loadingError/LoadingError';
import { useAccount } from '@contexts/AccountProvider';
import useGetTimeline from '@hooks/queries/useGetTimeline';
import useLoadMoreIfNecessary from '@hooks/useLoadMoreIfNecessary';
import getEnumTranslation from '@utils/getEnumTranslation';
import { toTitleCase } from '@utils/string/caseUtils';

export type TimelineFilters = {
    timelineDateStart?: string;
    timelineDateEnd?: string;
    timelineType?: string;
    categoryType?: string;
};

const AccountTimeline = () => {
    const { accountId } = useParams();
    const { isLoading: isLoadingAccounts } = useAccount();
    const [filterTimelineOpen, setFilterTimelineOpen] = useState(false);
    const [timelineFilters, setTimelineFilters] = useState(
        {} as TimelineFilters,
    );

    const { data, isError, isLoading, fetchNextPage, hasNextPage, refetch } =
        useGetTimeline({ accountId, ...timelineFilters });

    useLoadMoreIfNecessary({ fetchNextPage, hasNextPage, isLoading }, [
        data?.pages,
    ]);

    useEffect(() => {
        refetch();
    }, [refetch, timelineFilters]);

    if (isError) {
        return (
            <Box sx={{ mt: 10 }}>
                <LoadingError />
            </Box>
        );
    }

    const timeline =
        data?.pages
            ?.filter(Boolean)
            ?.map((page, index) =>
                page.items.map((aPage) => ({
                    next_cursor: data?.pages[index - 1]?.next_cursor,
                    ...aPage,
                })),
            )
            .flat(1) || [];

    const getLabel = (filter: [string, string | number | boolean]) => {
        if (filter[0] === 'timelineDateStart') {
            return (
                <>
                    <span style={{ marginRight: 2 }}>
                        {getEnumTranslation('from')}:
                    </span>
                    <DateFormatter
                        date={filter[1] as string}
                        format="shortDateAbr"
                    />
                </>
            );
        }

        if (filter[0] === 'timelineDateEnd') {
            return (
                <>
                    <span style={{ marginRight: 2 }}>
                        {getEnumTranslation('to')}:
                    </span>
                    <DateFormatter
                        date={filter[1] as string}
                        format="shortDateAbr"
                    />
                </>
            );
        }

        return toTitleCase(filter[1]);
    };

    const handleClick = () => setFilterTimelineOpen(true);

    return (
        <>
            <Box>
                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-end">
                    {Object.entries(timelineFilters).map(function (filter) {
                        if (filter[1] !== '' && filter[1] !== undefined) {
                            const filterProperty = filter[0];
                            return (
                                <Box mr={1.25} key={filterProperty}>
                                    <ChipButton
                                        label={getLabel(filter)}
                                        onDelete={() => {
                                            let defaultValue = '';
                                            if (
                                                filterProperty ===
                                                    'timelineDateStart' ||
                                                filterProperty ===
                                                    'timelineDateEnd'
                                            ) {
                                                defaultValue = undefined;
                                            }

                                            setTimelineFilters({
                                                ...timelineFilters,
                                                [filterProperty]: defaultValue,
                                            });
                                        }}
                                    />
                                </Box>
                            );
                        }
                    })}
                    <ChipButton
                        onClick={handleClick}
                        label={getEnumTranslation('filter')}
                        variant="editMenu"
                    />
                </Box>
            </Box>
            <Box>
                {(isLoading || isLoadingAccounts) && <TimelineSkeleton />}
                {!isLoading && !isLoadingAccounts && timeline?.length == 0 && (
                    <NoTimeline />
                )}
                {!isLoading && !isLoadingAccounts && !!timeline?.length && (
                    <InfiniteScroll
                        dataLength={timeline?.length}
                        next={fetchNextPage}
                        hasMore={hasNextPage}
                        endMessage={
                            <EndMessage
                                message={
                                    <FormattedMessage
                                        id="Accounttimelines.noMoretimelines"
                                        defaultMessage="No more timelines to show"
                                    />
                                }
                            />
                        }
                        loader={
                            <GenericLoadingText
                                displayText={
                                    <FormattedMessage
                                        id="AccountStatements.Loading"
                                        defaultMessage="Loading..."
                                    />
                                }
                                showLoader
                            />
                        }>
                        <Timeline timeline={timeline} showTooltip />
                    </InfiniteScroll>
                )}
            </Box>
            {filterTimelineOpen && (
                <TimelineFilterDialog
                    timelineFilters={timelineFilters}
                    setTimelineFilters={setTimelineFilters}
                    filterTimelineOpen={filterTimelineOpen}
                    setFilterTimelineOpen={setFilterTimelineOpen}
                    refetch={refetch}
                />
            )}
        </>
    );
};

export default AccountTimeline;
